import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { STATUS_LABEL } from "@/lib/utils-task";

export default function TaskStatusDialog({ open, onClose, task, onSaved }) {
  const [status, setStatus] = useState(task?.status || "Pending");
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && task) {
      setStatus(task.status);
      setRemark("");
    }
  }, [open, task]);

  const remarkRequired = status === "In-Progress" || status === "Done";
  const remarkLabel = useMemo(() => {
    if (status === "In-Progress") return "Reason for moving to In Progress";
    if (status === "Done") return "Completion remark";
    return "Remark (optional)";
  }, [status]);

  const submit = async (e) => {
    e.preventDefault();
    if (remarkRequired && !remark.trim()) {
      toast.error("A remark is required for this status.");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/tasks/${task.id}/status`, { status, remark: remark.trim() || null });
      toast.success("Status updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="task-status-dialog">
        <DialogHeader>
          <DialogTitle>Update status</DialogTitle>
          <DialogDescription className="text-xs text-slate-500 truncate">
            {task.title}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="label-eyebrow">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="status-dialog-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Pending">{STATUS_LABEL.Pending}</SelectItem>
                <SelectItem value="In-Progress">{STATUS_LABEL["In-Progress"]}</SelectItem>
                <SelectItem value="Done">{STATUS_LABEL.Done}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="label-eyebrow">
              {remarkLabel}
              {remarkRequired && <span className="text-rose-600 ml-1">*</span>}
            </Label>
            <Textarea
              rows={4}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder={
                status === "In-Progress"
                  ? "e.g. Waiting on parts from central warehouse."
                  : status === "Done"
                  ? "e.g. Completed inspection. All checks passed."
                  : "Optional context"
              }
              data-testid="status-dialog-remark"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose} disabled={busy} data-testid="status-dialog-cancel">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="status-dialog-submit"
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
